import { Content, Editor } from '@tiptap/core';
import { EditorState, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';

import { applyOwnership, removeOwnership } from '../../../../lib/dom/selectors';
import { serializeRecord } from '../../../../lib/records/serialize-record';
import { parseState } from '../../../../lib/state/parse-state';
import { SuggestionProps, TagOptions } from '../types';
import { runIfFn } from './run-if-fn';

interface Store {
	component: ReactRenderer;
	isClicked: boolean;
	popup: ReturnType<typeof tippy>;
	props: SuggestionProps;
	timeout: NodeJS.Timeout;
	triggerPos: number;
}

interface UpdateViewOptions extends TagOptions {
	editor: Editor;
	key?: PluginKey;
	name: string;
	prevState: EditorState;
	store: Store;
	view: EditorView;
}

export const updateView = async ({
	allowanceBoostId,
	budget,
	component: Component,
	debounceFetch,
	editor,
	fetchData,
	insertOnClose = true,
	insertPrefixSpace = false,
	key,
	name,
	prevState,
	requireSelection,
	store,
	transformContent,
	uniqueId,
	view,
}: UpdateViewOptions) => {
	const prev = key?.getState(prevState);
	const next = key?.getState(view.state);

	// See how the state changed
	const moved = prev.active && next.active && prev.range.from !== next.range.from;
	const started = !prev.active && next.active;
	const stopped = prev.active && !next.active;
	const changed = !started && !stopped && prev.query !== next.query;
	const handleStart = started || moved;
	const handleChange = changed && !moved;
	const handleExit = stopped || moved;

	// Cancel when suggestion isn't active
	if (!handleStart && !handleChange && !handleExit) {
		return;
	}

	const state = handleExit && !handleStart ? prev : next;
	const decorationNode = view.dom.querySelector(`[data-decoration-id="${state.decorationId}"]`);

	// Isolates what's required to close the view as there are
	// situations where lower-order components need to initiate
	// closing without processing a command.
	const onClose = () => {
		removeOwnership(uniqueId);

		store.popup?.[0].destroy();
		store.component?.destroy();

		if (store.timeout) {
			clearTimeout(store.timeout);
		}
	};

	store.props = {
		hash: uniqueId,
		allowanceBoostId,
		budget,
		editor,
		range: state.range,
		query: state.query,
		text: state.text,
		prevItems: store.props?.items ?? [],
		items: [],
		isLoading: true,
		close: onClose,
		command: async (props) => {
			// Used to prevent automatic insertion of the node if the user clicks
			// on an item and insertOnClose is set to true. Normally this would
			// insert the node twice, once on click and once when the selector closes,
			// but would end up throwing a RangeError
			store.isClicked = true;

			const range = state.range;
			const nodeAfter = editor.view.state.selection.$to.nodeAfter;
			const overrideSpace = nodeAfter?.text?.startsWith(' ');

			if (overrideSpace) {
				range.to += 1;
			}

			let content: Content = [
				{
					type: name,
					attrs: props,
				},
				{
					type: 'text',
					text: ' ',
				},
			];

			if (transformContent) {
				content = await transformContent(props, content);
			}

			if (insertPrefixSpace && Array.isArray(content)) {
				const pos = store.triggerPos;
				const precedingChar = editor.view.state.doc.textBetween(pos - 1, pos);

				if (precedingChar.trim() !== '') {
					content.unshift({
						type: 'text',
						text: ' ',
					});
				}
			}

			editor.chain().focus().insertContentAt(range, content).run();

			window.getSelection()?.collapseToEnd();
		},
		decorationNode,
		// virtual node for popper.js or tippy.js
		// this can be used for building popups without a DOM node
		clientRect: decorationNode
			? () => {
					// because of `items` can be asynchrounous we’ll search for the current decoration node
					const { decorationId } = key?.getState(editor.state); // eslint-disable-line
					const currentDecorationNode = view.dom.querySelector(`[data-decoration-id="${decorationId}"]`);

					return currentDecorationNode?.getBoundingClientRect() || null;
					// eslint-disable-next-line
				}
			: null,
	};

	if (handleExit) {
		onClose();
		// When the selector closes (likely because the typed query does not satisfy the `matchQuery` option) then we can automatically insert
		// the node type. This is used for cases like hashtags and amounts, where the user is able to add a value that is not limited to the
		// presented list
		if (insertOnClose && !store.isClicked) {
			// If selection from items is required, then only insert data if a single item exists in the store
			if (runIfFn(requireSelection, state.query) && state.query) {
				const items = store.props.prevItems;

				if (items.length === 1) {
					const item = items[0];
					store.props.command(serializeRecord(item));
				}
			} else if (state.query) {
				// This is the current global position at the *end* of the node
				const pos = editor.view.state.selection.$to;
				const node = pos.nodeBefore;
				const text = node?.textContent || '';
				const index = text.indexOf(state.query);

				if (node && index !== -1) {
					// Find the starting global integer position by subtracting the length of the text content, then adding the starting index
					// The -1 adjusts for the initiating character
					const from = pos.pos - node.textContent.length + index - 1;

					// The +1 adjusts for the triggering space
					const to = from + state.query.length + 1;

					const content = [
						{
							type: name,
							attrs: {
								id: state.query,
							},
						},
						{
							type: 'text',
							text: ' ',
						},
					];

					// If the character preceding the trigger is not a space, then we need to add one
					if (insertPrefixSpace) {
						const pos = store.triggerPos;
						const precedingChar = editor.view.state.doc.textBetween(pos - 1, pos);

						if (precedingChar.trim() !== '') {
							content.unshift({
								type: 'text',
								text: ' ',
							});
						}
					}

					editor.chain().focus().insertContentAt({ from, to }, content).run();
				}
			}
		}
	}

	if (handleStart) {
		store.isClicked = false;

		// Component will be created with a wrapper using ID `selector-ID`. We need to add this relationship to the
		// editor itself for aria attributes to work correctly. Because of the nature of ProseMirror, which runs
		// outside the React lifecycle, we need to use document query selectors to find the element
		applyOwnership(uniqueId);

		// Save the location just before the trigger character
		store.triggerPos = editor.view.state.selection.$to.pos - 1;

		store.component = new ReactRenderer(Component, {
			props: store.props,
			editor: store.props.editor,
		});

		if (!store.props.clientRect) {
			return;
		}

		store.popup = (tippy as any)('body', {
			getReferenceClientRect: store.props.clientRect,
			appendTo: () => document.body,
			content: store.component.element,
			showOnCreate: true,
			interactive: true,
			trigger: 'manual',
			placement: 'bottom-start',
			maxWidth: 'none',
			popperOptions: {
				modifiers: [
					{
						name: 'flip',
						allowedAutoPlacements: ['bottom-start', 'bottom-end', 'top-start', 'top-end', 'auto'],
					},
					{
						name: 'preventOverflow',
						options: {
							altAxis: true,
							tether: false,
						},
					},
				],
			},
		});
	}

	if (handleChange || handleStart) {
		if (store.timeout) {
			clearTimeout(store.timeout);
		}

		let debounceTime = 0;

		if (typeof debounceFetch === 'number') {
			debounceTime = debounceFetch;
		} else if (debounceFetch) {
			debounceTime = 800;
		}

		store.timeout = setTimeout(async () => {
			store.props.isLoading = true;
			store.props.items = store.props.prevItems;
			store.component.updateProps(store.props);

			const items = await fetchData({
				editor,
				query: state.query,
				state: parseState(editor),
			});
			store.props.items = items;
			store.props.isLoading = false;
			store.component.updateProps(store.props);

			if (!store.props.clientRect) {
				return;
			}

			if (!store.popup[0].state.isDestroyed) {
				store.popup[0].setProps({
					getReferenceClientRect: store.props.clientRect as any,
				});
			}
		}, debounceTime);
	}
};
